import { useMemo } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts'
import { formatUTCToLocal, formatChartTimestamp } from '@/utils/date'
import { Thermometer, Droplets, Battery, TrendingUp, TrendingDown, Minus, Wind, Gauge, CloudRain, Sun, Zap, Sprout } from 'lucide-react'
import type { Rk900Measurement } from '@/types/measurement.types'
import type { Device } from '@/types/device.types'
import { WindRoseChart } from '../charts/WindRoseChart'
import { useUserUnits, convertTemp, formatTemp, tempLabel, convertSpeed, formatSpeed, speedLabel, speedUnitForWindRose } from '@/utils/units'

interface RK900ViewProps {
  device: Device
  measurements: Rk900Measurement[]
}

export const RK900View = ({ device, measurements }: RK900ViewProps) => {
  const units = useUserUnits()
  const latestMeasurement = measurements[0]

  // Helper function to safely convert to number and format
  const toNum = (value: any): number | undefined => {
    if (value === null || value === undefined) return undefined
    const num = typeof value === 'number' ? value : Number(value)
    return isNaN(num) ? undefined : num
  }

  const chartData = useMemo(() => {
    return measurements
      .slice()
      .reverse()
      .map((m) => {
        const temp = toNum(m.temperature)
        const ws = toNum(m.windSpeed)
        const st = toNum(m.sm1Temperature)
        return {
          timestamp: formatChartTimestamp(m.measurementDateUtc),
          temperature: temp !== undefined ? convertTemp(temp, units.temperature) : undefined,
          humidity: toNum(m.humidity),
          battery: (toNum(m.batteryVoltage) || 0) / 1000, // Convert mV to V
          pressure: toNum(m.pressure),
          windSpeed: ws !== undefined ? convertSpeed(ws, units.speed) : undefined,
          rainfall: toNum(m.rainfallMm),
          radiation: toNum(m.radiation),
          light: toNum(m.light),
          pm25: toNum(m.airQualityPm2p5),
          pm10: toNum(m.airQualityPm10),
          soilMoisture: toNum(m.sm1Moisture),
          soilTemp: st !== undefined ? convertTemp(st, units.temperature) : undefined,
        }
      })
  }, [measurements, units.temperature, units.speed])

  // Calculate temperature domain
  const tempDomain = useMemo(() => {
    const temps = chartData
      .map(d => d.temperature)
      .filter((t): t is number => t !== undefined && !isNaN(t))

    if (temps.length === 0) {
      return [0, 40]
    }

    const min = Math.min(...temps)
    const max = Math.max(...temps)
    const range = max - min
    const padding = Math.max(range * 0.2, 5) // 20% padding or min 5 degrees

    const domain = [Math.floor(min - padding), Math.ceil(max + padding)] as [number, number]

    return domain
  }, [chartData])

  const stats = useMemo(() => {
    if (measurements.length === 0) return null

    // Filter out undefined/null values and convert to numbers
    const temps = measurements
      .map(m => toNum(m.temperature))
      .filter((t): t is number => t !== undefined)
    const humidity = measurements
      .map(m => toNum(m.humidity))
      .filter((h): h is number => h !== undefined)

    // Need at least one valid measurement
    if (temps.length === 0 || humidity.length === 0) return null

    const avgTemp = temps.reduce((a, b) => a + b, 0) / temps.length
    const maxTemp = Math.max(...temps)
    const minTemp = Math.min(...temps)

    const avgHumidity = humidity.reduce((a, b) => a + b, 0) / humidity.length
    const maxHumidity = Math.max(...humidity)
    const minHumidity = Math.min(...humidity)

    // Calculate trend only if both values are defined
    const temp0 = toNum(measurements[0]?.temperature)
    const temp1 = toNum(measurements[1]?.temperature)
    const tempTrend = measurements.length > 1 && temp0 !== undefined && temp1 !== undefined
      ? temp0 - temp1
      : 0

    const hum0 = toNum(measurements[0]?.humidity)
    const hum1 = toNum(measurements[1]?.humidity)
    const humidityTrend = measurements.length > 1 && hum0 !== undefined && hum1 !== undefined
      ? hum0 - hum1
      : 0

    return {
      temp: { avg: avgTemp, max: maxTemp, min: minTemp, trend: tempTrend },
      humidity: { avg: avgHumidity, max: maxHumidity, min: minHumidity, trend: humidityTrend }
    }
  }, [measurements])

  const getTrendIcon = (trend: number) => {
    if (trend > 0.5) return <TrendingUp className="h-4 w-4 text-red-500" />
    if (trend < -0.5) return <TrendingDown className="h-4 w-4 text-blue-500" />
    return <Minus className="h-4 w-4 text-gray-500" />
  }

  const getComfortLevel = (temp?: number, humidity?: number) => {
    if (!temp || !humidity) return 'Unknown'

    // Simple comfort calculation based on temperature and humidity
    if (temp >= 20 && temp <= 26 && humidity >= 30 && humidity <= 60) {
      return 'Comfortable'
    } else if ((temp >= 18 && temp <= 28) && (humidity >= 25 && humidity <= 70)) {
      return 'Moderate'
    } else {
      return 'Uncomfortable'
    }
  }

  const comfortLevel = getComfortLevel(toNum(latestMeasurement?.temperature), toNum(latestMeasurement?.humidity))
  const comfortColor = comfortLevel === 'Comfortable' ? 'text-green-600' :
                       comfortLevel === 'Moderate' ? 'text-yellow-600' : 'text-red-600'

  return (
    <div className="space-y-6">
      {/* Current Status Cards */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Temperature</CardTitle>
            <Thermometer className="h-4 w-4 text-orange-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {(() => {
                const temp = toNum(latestMeasurement?.temperature)
                return temp !== undefined ? formatTemp(temp, units.temperature) : 'N/A'
              })()}
            </div>
            <p className="text-xs text-muted-foreground flex items-center gap-1">
              {stats && (
                <>
                  {getTrendIcon(stats.temp.trend)}
                  {stats.temp.trend > 0 ? '+' : ''}{convertTemp(stats.temp.trend, units.temperature).toFixed(1)}{tempLabel(units.temperature)}
                </>
              )}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Humidity</CardTitle>
            <Droplets className="h-4 w-4 text-blue-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {(() => {
                const hum = toNum(latestMeasurement?.humidity)
                return hum !== undefined ? `${hum.toFixed(1)}%` : 'N/A'
              })()}
            </div>
            <p className="text-xs text-muted-foreground flex items-center gap-1">
              {stats && (
                <>
                  {getTrendIcon(stats.humidity.trend)}
                  {stats.humidity.trend > 0 ? '+' : ''}{stats.humidity.trend.toFixed(1)}%
                </>
              )}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Comfort Level</CardTitle>
            <Badge variant="outline" className={comfortColor}>
              {comfortLevel}
            </Badge>
          </CardHeader>
          <CardContent>
            {stats ? (
              <div className="text-sm">
                <div>Temp Range: {formatTemp(stats.temp.min, units.temperature)} - {formatTemp(stats.temp.max, units.temperature)}</div>
                <div>Humidity: {stats.humidity.min.toFixed(0)}% - {stats.humidity.max.toFixed(0)}%</div>
              </div>
            ) : (
              <div className="text-sm text-muted-foreground">No data available</div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Battery</CardTitle>
            <Battery className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {(() => {
                const batt = toNum(latestMeasurement?.batteryVoltage)
                return batt !== undefined ? `${(batt / 1000).toFixed(2)}V` : 'N/A'
              })()}
            </div>
            <p className="text-xs text-muted-foreground">
              Last: {latestMeasurement ? formatUTCToLocal(latestMeasurement.measurementDateUtc, 'HH:mm') : ''}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Additional Weather Station Metrics */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        {(() => {
          const pressure = toNum(latestMeasurement?.pressure)
          return pressure !== undefined && (
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Pressure</CardTitle>
                <Gauge className="h-4 w-4 text-purple-500" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {pressure.toFixed(1)} hPa
                </div>
                <p className="text-xs text-muted-foreground">
                  Atmospheric pressure
                </p>
              </CardContent>
            </Card>
          )
        })()}

        {(() => {
          const windSpeed = toNum(latestMeasurement?.windSpeed)
          const windDir = toNum(latestMeasurement?.windDirection)
          return windSpeed !== undefined && (
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Wind Speed</CardTitle>
                <Wind className="h-4 w-4 text-cyan-500" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {formatSpeed(windSpeed, units.speed)}
                </div>
                <p className="text-xs text-muted-foreground">
                  {windDir !== undefined ? `Direction: ${windDir.toFixed(0)}°` : 'Wind velocity'}
                </p>
              </CardContent>
            </Card>
          )
        })()}

        {(() => {
          // Calculate total rainfall for the period
          const totalRainfall = measurements.reduce((sum, m) => {
            const rain = toNum(m.rainfallMm)
            return sum + (rain || 0)
          }, 0)

          return totalRainfall > 0 && (
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Rainfall</CardTitle>
                <CloudRain className="h-4 w-4 text-blue-600" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {totalRainfall.toFixed(1)} mm
                </div>
                <p className="text-xs text-muted-foreground">
                  Total for period
                </p>
              </CardContent>
            </Card>
          )
        })()}

        {(() => {
          const radiation = toNum(latestMeasurement?.radiation)
          const light = toNum(latestMeasurement?.light)
          return radiation !== undefined && (
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Solar Radiation</CardTitle>
                <Sun className="h-4 w-4 text-yellow-500" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {radiation.toFixed(0)} W/m²
                </div>
                <p className="text-xs text-muted-foreground">
                  {light !== undefined ? `Light: ${light.toFixed(0)} lux` : 'Solar irradiance'}
                </p>
              </CardContent>
            </Card>
          )
        })()}

        {(() => {
          const pm25 = toNum(latestMeasurement?.airQualityPm2p5)
          const pm10 = toNum(latestMeasurement?.airQualityPm10)
          return pm25 !== undefined && (
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Air Quality PM2.5</CardTitle>
                <Zap className="h-4 w-4 text-amber-500" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {pm25.toFixed(1)} μg/m³
                </div>
                <p className="text-xs text-muted-foreground">
                  {pm10 !== undefined ? `PM10: ${pm10.toFixed(1)} μg/m³` : 'Particulate matter'}
                </p>
              </CardContent>
            </Card>
          )
        })()}

        {(() => {
          const soilMoisture = toNum(latestMeasurement?.sm1Moisture)
          const soilTemp = toNum(latestMeasurement?.sm1Temperature)
          return soilMoisture !== undefined && (
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Soil Moisture</CardTitle>
                <Sprout className="h-4 w-4 text-green-600" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {soilMoisture.toFixed(1)}%
                </div>
                <p className="text-xs text-muted-foreground">
                  {soilTemp !== undefined ? `Soil Temp: ${formatTemp(soilTemp, units.temperature)}` : 'Soil sensor 1'}
                </p>
              </CardContent>
            </Card>
          )
        })()}

        {(() => {
          const solarV = toNum(latestMeasurement?.solarVoltage)
          return solarV !== undefined && (
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Solar Panel</CardTitle>
                <Sun className="h-4 w-4 text-orange-500" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {(solarV / 1000).toFixed(2)}V
                </div>
                <p className="text-xs text-muted-foreground">
                  {latestMeasurement.solarCharge ? '⚡ Charging' : 'Not charging'}
                </p>
              </CardContent>
            </Card>
          )
        })()}
      </div>

      {/* Temperature & Humidity Chart */}
      <Card>
        <CardHeader>
          <CardTitle>Temperature & Humidity Over Time</CardTitle>
          <CardDescription>
            Environmental conditions tracked by the RK900 sensor
          </CardDescription>
        </CardHeader>
        <CardContent>
          {chartData.length > 0 ? (
            <ResponsiveContainer width="100%" height={450}>
              <LineChart data={chartData} margin={{ top: 20, right: 80, left: 80, bottom: 80 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="timestamp"
                  style={{ fontSize: '12px' }}
                  angle={-45}
                  textAnchor="end"
                  height={60}
                />
                <YAxis
                  yAxisId="temp"
                  style={{ fontSize: '12px' }}
                  label={{ value: `Temperature (${tempLabel(units.temperature)})`, angle: -90, position: 'insideLeft', style: { textAnchor: 'middle' } }}
                  domain={tempDomain}
                  width={60}
                  tickCount={8}
                />
                <YAxis
                  yAxisId="humidity"
                  orientation="right"
                  style={{ fontSize: '12px' }}
                  label={{ value: 'Humidity (%)', angle: 90, position: 'insideRight', style: { textAnchor: 'middle' } }}
                  domain={[0, 100]}
                  width={60}
                  tickCount={6}
                />
                <Tooltip
                  formatter={(value: any, name: string) => {
                    const num = toNum(value)
                    if (num === undefined) return ['N/A', name]
                    if (name === 'Temperature') return [`${num.toFixed(1)}${tempLabel(units.temperature)}`, name]
                    if (name === 'Humidity') return [`${num.toFixed(1)}%`, name]
                    return [num, name]
                  }}
                />
                <Legend verticalAlign="bottom" wrapperStyle={{ paddingTop: '20px' }} />
                <Line
                  yAxisId="temp"
                  type="monotone"
                  dataKey="temperature"
                  stroke="#f97316"
                  name="Temperature"
                  strokeWidth={2}
                  dot={false}
                />
                <Line
                  yAxisId="humidity"
                  type="monotone"
                  dataKey="humidity"
                  stroke="#3b82f6"
                  name="Humidity"
                  strokeWidth={2}
                  dot={false}
                />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex items-center justify-center h-[450px] text-muted-foreground">
              No measurement data available
            </div>
          )}
        </CardContent>
      </Card>

      {/* Statistics Summary */}
      <Card>
        <CardHeader>
          <CardTitle>Statistics Summary</CardTitle>
          <CardDescription>
            Summary of environmental conditions for the selected time period
          </CardDescription>
        </CardHeader>
        <CardContent>
          {stats ? (
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <h4 className="font-medium flex items-center gap-2">
                  <Thermometer className="h-4 w-4" />
                  Temperature
                </h4>
                <div className="grid grid-cols-3 gap-2 text-sm">
                  <div>
                    <p className="text-muted-foreground">Average</p>
                    <p className="font-medium">{formatTemp(stats.temp.avg, units.temperature)}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Minimum</p>
                    <p className="font-medium">{formatTemp(stats.temp.min, units.temperature)}</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Maximum</p>
                    <p className="font-medium">{formatTemp(stats.temp.max, units.temperature)}</p>
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <h4 className="font-medium flex items-center gap-2">
                  <Droplets className="h-4 w-4" />
                  Humidity
                </h4>
                <div className="grid grid-cols-3 gap-2 text-sm">
                  <div>
                    <p className="text-muted-foreground">Average</p>
                    <p className="font-medium">{stats.humidity.avg.toFixed(1)}%</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Minimum</p>
                    <p className="font-medium">{stats.humidity.min.toFixed(1)}%</p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Maximum</p>
                    <p className="font-medium">{stats.humidity.max.toFixed(1)}%</p>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <p className="text-muted-foreground text-center">No data available</p>
          )}
        </CardContent>
      </Card>

      {/* Pressure & Wind Chart */}
      {chartData.some(d => d.pressure !== undefined || d.windSpeed !== undefined) && (
        <Card>
          <CardHeader>
            <CardTitle>Atmospheric Pressure & Wind Speed</CardTitle>
            <CardDescription>
              Pressure and wind measurements over time
            </CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={400}>
              <LineChart data={chartData} margin={{ top: 20, right: 80, left: 80, bottom: 80 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="timestamp"
                  style={{ fontSize: '12px' }}
                  angle={-45}
                  textAnchor="end"
                  height={60}
                />
                <YAxis
                  yAxisId="pressure"
                  style={{ fontSize: '12px' }}
                  label={{ value: 'Pressure (hPa)', angle: -90, position: 'insideLeft', style: { textAnchor: 'middle' } }}
                  width={60}
                  tickCount={8}
                />
                <YAxis
                  yAxisId="wind"
                  orientation="right"
                  style={{ fontSize: '12px' }}
                  label={{ value: `Wind Speed (${speedLabel(units.speed)})`, angle: 90, position: 'insideRight', style: { textAnchor: 'middle' } }}
                  width={60}
                  tickCount={8}
                />
                <Tooltip
                  formatter={(value: any, name: string) => {
                    const num = toNum(value)
                    if (num === undefined) return ['N/A', name]
                    if (name === 'Pressure') return [`${num.toFixed(1)} hPa`, name]
                    if (name === 'Wind Speed') return [`${num.toFixed(1)} ${speedLabel(units.speed)}`, name]
                    return [num, name]
                  }}
                />
                <Legend verticalAlign="bottom" wrapperStyle={{ paddingTop: '20px' }} />
                <Line
                  yAxisId="pressure"
                  type="monotone"
                  dataKey="pressure"
                  stroke="#a855f7"
                  name="Pressure"
                  strokeWidth={2}
                  dot={false}
                  connectNulls
                />
                <Line
                  yAxisId="wind"
                  type="monotone"
                  dataKey="windSpeed"
                  stroke="#06b6d4"
                  name="Wind Speed"
                  strokeWidth={2}
                  dot={false}
                  connectNulls
                />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}

      {/* Wind Rose Chart */}
      {measurements.some(m => toNum(m.windSpeed) !== undefined && toNum(m.windDirection) !== undefined) && (
        <Card>
          <CardHeader>
            <CardTitle>Wind Rose</CardTitle>
            <CardDescription>
              Wind speed and direction distribution for the selected time period
            </CardDescription>
          </CardHeader>
          <CardContent>
            <WindRoseChart
              data={measurements.map(m => ({
                windSpeed: toNum(m.windSpeed) || 0,
                windDirection: toNum(m.windDirection) || 0
              }))}
              userSpeedUnit={speedUnitForWindRose(units.speed)}
            />
          </CardContent>
        </Card>
      )}

      {/* Rainfall Chart */}
      {chartData.some(d => d.rainfall !== undefined) && (
        <Card>
          <CardHeader>
            <CardTitle>Rainfall</CardTitle>
            <CardDescription>
              Precipitation measurements over time
            </CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={350}>
              <LineChart data={chartData} margin={{ top: 5, right: 30, left: 20, bottom: 45 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="timestamp"
                  style={{ fontSize: '12px' }}
                  angle={-45}
                  textAnchor="end"
                  height={60}
                />
                <YAxis
                  style={{ fontSize: '12px' }}
                  label={{ value: 'Rainfall (mm)', angle: -90, position: 'insideLeft' }}
                  width={60}
                  tickCount={8}
                />
                <Tooltip
                  formatter={(value: any) => {
                    const num = toNum(value)
                    return num !== undefined ? [`${num.toFixed(1)} mm`, 'Rainfall'] : ['N/A', 'Rainfall']
                  }}
                />
                <Line
                  type="monotone"
                  dataKey="rainfall"
                  stroke="#2563eb"
                  name="Rainfall"
                  strokeWidth={2}
                  dot={false}
                  connectNulls
                />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}

      {/* Air Quality Chart */}
      {chartData.some(d => d.pm25 !== undefined || d.pm10 !== undefined) && (
        <Card>
          <CardHeader>
            <CardTitle>Air Quality</CardTitle>
            <CardDescription>
              PM2.5 and PM10 particulate matter measurements
            </CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={350}>
              <LineChart data={chartData} margin={{ top: 5, right: 30, left: 20, bottom: 80 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="timestamp"
                  style={{ fontSize: '12px' }}
                  angle={-45}
                  textAnchor="end"
                  height={60}
                />
                <YAxis
                  style={{ fontSize: '12px' }}
                  label={{ value: 'PM (μg/m³)', angle: -90, position: 'insideLeft' }}
                  width={60}
                  tickCount={8}
                />
                <Tooltip
                  formatter={(value: any, name: string) => {
                    const num = toNum(value)
                    return num !== undefined ? [`${num.toFixed(1)} μg/m³`, name] : ['N/A', name]
                  }}
                />
                <Legend verticalAlign="bottom" wrapperStyle={{ paddingTop: '20px' }} />
                <Line
                  type="monotone"
                  dataKey="pm25"
                  stroke="#f59e0b"
                  name="PM2.5"
                  strokeWidth={2}
                  dot={false}
                  connectNulls
                />
                <Line
                  type="monotone"
                  dataKey="pm10"
                  stroke="#dc2626"
                  name="PM10"
                  strokeWidth={2}
                  dot={false}
                  connectNulls
                />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}

      {/* Soil Moisture & Temperature Chart */}
      {chartData.some(d => d.soilMoisture !== undefined || d.soilTemp !== undefined) && (
        <>
          <Card>
            <CardHeader>
              <CardTitle>Soil Conditions</CardTitle>
              <CardDescription>
                Soil moisture and temperature measurements
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={350}>
                <LineChart data={chartData} margin={{ top: 20, right: 80, left: 80, bottom: 80 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="timestamp"
                    style={{ fontSize: '12px' }}
                    angle={-45}
                    textAnchor="end"
                    height={60}
                  />
                  <YAxis
                    yAxisId="moisture"
                    style={{ fontSize: '12px' }}
                    label={{ value: 'Moisture (%)', angle: -90, position: 'insideLeft', style: { textAnchor: 'middle' } }}
                    width={60}
                    tickCount={6}
                  />
                  <YAxis
                    yAxisId="temp"
                    orientation="right"
                    style={{ fontSize: '12px' }}
                    label={{ value: `Temperature (${tempLabel(units.temperature)})`, angle: 90, position: 'insideRight', style: { textAnchor: 'middle' } }}
                    width={60}
                    tickCount={8}
                  />
                  <Tooltip
                    formatter={(value: any, name: string) => {
                      const num = toNum(value)
                      if (num === undefined) return ['N/A', name]
                      if (name === 'Soil Moisture') return [`${num.toFixed(1)}%`, name]
                      if (name === 'Soil Temperature') return [`${num.toFixed(1)}${tempLabel(units.temperature)}`, name]
                      return [num, name]
                    }}
                  />
                  <Legend verticalAlign="bottom" wrapperStyle={{ paddingTop: '20px' }} />
                  <Line
                    yAxisId="moisture"
                    type="monotone"
                    dataKey="soilMoisture"
                    stroke="#16a34a"
                    name="Soil Moisture"
                    strokeWidth={2}
                    dot={false}
                    connectNulls
                  />
                  <Line
                    yAxisId="temp"
                    type="monotone"
                    dataKey="soilTemp"
                    stroke="#ea580c"
                    name="Soil Temperature"
                    strokeWidth={2}
                    dot={false}
                    connectNulls
                  />
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>

          {/* Soil Condition Analysis */}
          {(() => {
            const soilMoisture = toNum(latestMeasurement?.sm1Moisture)
            const soilTemp = toNum(latestMeasurement?.sm1Temperature)

            if (soilMoisture === undefined) return null

            const getSoilAnalysis = (moisture: number, temp?: number) => {
              // Soil type thresholds based on agricultural research
              const soilTypes = {
                sandy: {
                  name: 'Sandy Soil',
                  saturation: 35,
                  fieldCapacity: 15,
                  wiltingPoint: 5,
                  optimal: { min: 8, max: 12 }
                },
                loamy: {
                  name: 'Loamy Soil',
                  saturation: 50,
                  fieldCapacity: 30,
                  wiltingPoint: 15,
                  optimal: { min: 20, max: 25 }
                },
                clay: {
                  name: 'Clay Soil',
                  saturation: 60,
                  fieldCapacity: 45,
                  wiltingPoint: 25,
                  optimal: { min: 30, max: 40 }
                }
              }

              const analyses: Array<{ soilType: string; status: string; description: string; severity: 'good' | 'warning' | 'danger' }> = []

              Object.entries(soilTypes).forEach(([key, soil]) => {
                let status = ''
                let description = ''
                let severity: 'good' | 'warning' | 'danger' = 'good'

                if (moisture > soil.saturation) {
                  status = 'Waterlogged'
                  description = `At ${moisture.toFixed(1)}%, the soil is oversaturated and likely waterlogged. This exceeds the saturation point of ${soil.saturation}% for ${soil.name}. Poor drainage and lack of oxygen can damage plant roots and promote root rot. Consider improving drainage or reducing irrigation.`
                  severity = 'danger'
                } else if (moisture >= soil.fieldCapacity) {
                  status = 'Very Wet'
                  description = `Moisture is ${moisture.toFixed(1)}%, which is above field capacity (${soil.fieldCapacity}%) for ${soil.name}. The soil is holding more water than it can retain against gravity. This may indicate recent heavy rainfall or over-irrigation. Most plants can tolerate this temporarily, but prolonged saturation can cause issues.`
                  severity = 'warning'
                } else if (moisture >= soil.optimal.min && moisture <= soil.optimal.max) {
                  status = 'Optimal'
                  description = `At ${moisture.toFixed(1)}%, the soil moisture is in the optimal range (${soil.optimal.min}-${soil.optimal.max}%) for ${soil.name}. This provides good water availability for plants while maintaining adequate air spaces in the soil for root respiration. Ideal for most crops and vegetation.`
                  severity = 'good'
                } else if (moisture > soil.optimal.max && moisture < soil.fieldCapacity) {
                  status = 'Moist'
                  description = `Moisture is ${moisture.toFixed(1)}%, which is above optimal (${soil.optimal.min}-${soil.optimal.max}%) but below field capacity for ${soil.name}. The soil has good water availability. Most plants will thrive, though some water-sensitive species may prefer slightly drier conditions.`
                  severity = 'good'
                } else if (moisture >= soil.wiltingPoint && moisture < soil.optimal.min) {
                  status = 'Dry'
                  description = `At ${moisture.toFixed(1)}%, the soil is drier than optimal (${soil.optimal.min}-${soil.optimal.max}%) for ${soil.name}. Water is still available to plants, but they may begin to experience mild stress. Consider irrigation soon, especially for shallow-rooted plants or during hot weather.`
                  severity = 'warning'
                } else if (moisture < soil.wiltingPoint) {
                  status = 'Very Dry'
                  description = `Moisture is ${moisture.toFixed(1)}%, below the wilting point (${soil.wiltingPoint}%) for ${soil.name}. Plants cannot extract sufficient water from the soil and will wilt. Immediate irrigation is needed to prevent permanent damage to vegetation. Prolonged drought stress can be fatal to plants.`
                  severity = 'danger'
                }

                // Add temperature context if available
                if (temp !== undefined) {
                  if (temp < 10) {
                    description += ` Note: Soil temperature is ${formatTemp(temp, units.temperature)}, which is relatively cool. Cold soils reduce water uptake by plant roots even when moisture is adequate.`
                  } else if (temp > 30) {
                    description += ` Note: Soil temperature is ${formatTemp(temp, units.temperature)}, which is quite warm. High temperatures increase evaporation rates and water demand by plants.`
                  }
                }

                analyses.push({
                  soilType: soil.name,
                  status,
                  description,
                  severity
                })
              })

              return analyses
            }

            const analyses = getSoilAnalysis(soilMoisture, soilTemp)

            return (
              <Card>
                <CardHeader>
                  <CardTitle>Soil Moisture Analysis</CardTitle>
                  <CardDescription>
                    Interpretation of current soil moisture levels based on common soil types
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="text-sm text-muted-foreground mb-4">
                      Current Reading: <span className="font-semibold text-foreground">{soilMoisture.toFixed(1)}%</span>
                      {soilTemp !== undefined && (
                        <> • Soil Temperature: <span className="font-semibold text-foreground">{formatTemp(soilTemp, units.temperature)}</span></>
                      )}
                    </div>

                    {analyses.map((analysis, idx) => (
                      <div key={idx} className="border-l-4 pl-4 py-2" style={{
                        borderColor: analysis.severity === 'good' ? '#16a34a' :
                                   analysis.severity === 'warning' ? '#f59e0b' : '#dc2626'
                      }}>
                        <div className="flex items-center gap-2 mb-2">
                          <h4 className="font-semibold">{analysis.soilType}</h4>
                          <Badge
                            variant={analysis.severity === 'good' ? 'default' : 'destructive'}
                            className={
                              analysis.severity === 'good' ? 'bg-green-600' :
                              analysis.severity === 'warning' ? 'bg-yellow-600' : 'bg-red-600'
                            }
                          >
                            {analysis.status}
                          </Badge>
                        </div>
                        <p className="text-sm text-muted-foreground leading-relaxed">
                          {analysis.description}
                        </p>
                      </div>
                    ))}

                    <div className="mt-6 p-4 bg-muted rounded-lg text-sm">
                      <h5 className="font-semibold mb-2">Understanding the Analysis:</h5>
                      <ul className="list-disc pl-5 space-y-1 text-muted-foreground">
                        <li><strong>Saturation Point:</strong> Maximum water the soil can hold when fully saturated</li>
                        <li><strong>Field Capacity:</strong> Water content after excess water has drained by gravity (24-48 hours after saturation)</li>
                        <li><strong>Optimal Range:</strong> Ideal moisture level for most plants, providing adequate water and oxygen</li>
                        <li><strong>Wilting Point:</strong> Minimum moisture level below which plants cannot extract water and will wilt</li>
                      </ul>
                      <p className="mt-3 text-xs italic">
                        Note: This analysis is based on typical characteristics of each soil type. Your specific soil composition,
                        crop requirements, and local climate conditions may vary. Use this as a general guide and adjust based on
                        observed plant health and local agricultural recommendations.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )
          })()}
        </>
      )}

      {/* Battery Voltage Chart */}
      {chartData.some(d => d.battery !== undefined && d.battery > 0) && (
        <Card>
          <CardHeader>
            <CardTitle>Battery Voltage</CardTitle>
            <CardDescription>
              Device battery voltage over time
            </CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={350}>
              <LineChart data={chartData} margin={{ top: 5, right: 30, left: 20, bottom: 45 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="timestamp"
                  style={{ fontSize: '12px' }}
                  angle={-45}
                  textAnchor="end"
                  height={60}
                />
                <YAxis
                  style={{ fontSize: '12px' }}
                  label={{ value: 'Voltage (V)', angle: -90, position: 'insideLeft' }}
                  width={60}
                  tickCount={8}
                />
                <Tooltip
                  formatter={(value: any) => {
                    const num = toNum(value)
                    return num !== undefined ? `${num.toFixed(2)}V` : 'N/A'
                  }}
                />
                <Line
                  type="monotone"
                  dataKey="battery"
                  stroke="#f59e0b"
                  name="Battery"
                  strokeWidth={2}
                  dot={false}
                />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}
    </div>
  )
}